import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Bell,
  Shield,
  Database,
  Palette,
  Globe,
  Users,
  Save,
  RefreshCw,
  CheckCircle2,
  Eye,
  Upload,
  Trash2,
  Plus,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Settings sections
const settingsSections = [
  { id: 'general', name: 'General', icon: SettingsIcon, description: 'Basic series settings' },
  { id: 'notifications', name: 'Notifications', icon: Bell, description: 'Email and alert preferences' },
  { id: 'users', name: 'Users & Permissions', icon: Users, description: 'Manage team access' },
  { id: 'integrations', name: 'Integrations', icon: Globe, description: 'External service connections' },
  { id: 'appearance', name: 'Appearance', icon: Palette, description: 'Branding and display' },
  { id: 'data', name: 'Data Management', icon: Database, description: 'Import, export, and backups' },
  { id: 'security', name: 'Security', icon: Shield, description: 'Authentication and access' },
];

// Mock users data
const mockUsers = [
  { id: 1, name: 'Chris Owens', email: 'cowens@worldracinggroup.com', role: 'Admin', status: 'active', lastLogin: '2025-12-10T09:30:00' },
  { id: 2, name: 'John Smith', email: 'jsmith@worldracinggroup.com', role: 'Editor', status: 'active', lastLogin: '2025-12-09T14:15:00' },
  { id: 3, name: 'Sarah Johnson', email: 'sjohnson@worldracinggroup.com', role: 'Viewer', status: 'active', lastLogin: '2025-12-08T11:45:00' },
  { id: 4, name: 'Mike Davis', email: 'mdavis@worldracinggroup.com', role: 'Editor', status: 'invited', lastLogin: null },
];

// Role options
const roleOptions = [
  { id: 'admin', label: 'Admin', description: 'Full access to all features' },
  { id: 'editor', label: 'Editor', description: 'Can edit events, entries, and results' },
  { id: 'viewer', label: 'Viewer', description: 'Read-only access' },
];

// Toggle Switch Component
function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="font-medium text-slate-700">{label}</div>
        {description && <div className="text-sm text-slate-500">{description}</div>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${
          checked ? 'bg-rms-accent' : 'bg-slate-300'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

// User Row Component
function UserRow({ user, onEdit, onRemove }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-rms-accent/10 flex items-center justify-center">
          <span className="text-sm font-semibold text-rms-accent">
            {user.name.split(' ').map(n => n[0]).join('')}
          </span>
        </div>
        <div>
          <div className="font-medium text-slate-800">{user.name}</div>
          <div className="text-sm text-slate-500">{user.email}</div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Badge variant={user.status === 'active' ? 'success' : 'warning'}>
          {user.status === 'active' ? 'Active' : 'Pending'}
        </Badge>
        <span className="text-sm text-slate-500 w-20">{user.role}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(user)}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onRemove(user)}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// General Settings Section
function GeneralSettings({ settings, onChange }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Series Display Name</label>
        <Input
          value={settings.displayName || ''}
          onChange={(e) => onChange({ ...settings, displayName: e.target.value })}
          placeholder="World of Outlaws Late Models"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Short Name</label>
        <Input
          value={settings.shortName || ''}
          onChange={(e) => onChange({ ...settings, shortName: e.target.value })}
          placeholder="WoO LM"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Current Season</label>
        <Select
          value={settings.currentSeason || '2026'}
          onChange={(e) => onChange({ ...settings, currentSeason: e.target.value })}
        >
          <option value="2026">2026</option>
          <option value="2025">2025</option>
          <option value="2024">2024</option>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Default Timezone</label>
        <Select
          value={settings.timezone || 'America/New_York'}
          onChange={(e) => onChange({ ...settings, timezone: e.target.value })}
        >
          <option value="America/New_York">Eastern Time (ET)</option>
          <option value="America/Chicago">Central Time (CT)</option>
          <option value="America/Denver">Mountain Time (MT)</option>
          <option value="America/Los_Angeles">Pacific Time (PT)</option>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Series Description</label>
        <Textarea
          value={settings.description || ''}
          onChange={(e) => onChange({ ...settings, description: e.target.value })}
          placeholder="Brief description of the series..."
          rows={3}
        />
      </div>
    </div>
  );
}

// Notifications Settings Section
function NotificationsSettings({ settings, onChange }) {
  return (
    <div className="space-y-4">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="font-medium text-slate-800 mb-3">Email Notifications</h3>
        <div className="space-y-1">
          <Toggle
            label="Event Reminders"
            description="Send reminders 24 hours before events"
            checked={settings.eventReminders ?? true}
            onChange={(val) => onChange({ ...settings, eventReminders: val })}
          />
          <Toggle
            label="Entry Notifications"
            description="Notify when new entries are submitted"
            checked={settings.entryNotifications ?? true}
            onChange={(val) => onChange({ ...settings, entryNotifications: val })}
          />
          <Toggle
            label="Results Published"
            description="Notify when results are finalized"
            checked={settings.resultsNotifications ?? true}
            onChange={(val) => onChange({ ...settings, resultsNotifications: val })}
          />
          <Toggle
            label="Weekly Summary"
            description="Receive weekly activity digest"
            checked={settings.weeklySummary ?? false}
            onChange={(val) => onChange({ ...settings, weeklySummary: val })}
          />
        </div>
      </div>

      <div>
        <h3 className="font-medium text-slate-800 mb-3">Alert Preferences</h3>
        <div className="space-y-1">
          <Toggle
            label="Browser Notifications"
            description="Show desktop notifications"
            checked={settings.browserNotifications ?? true}
            onChange={(val) => onChange({ ...settings, browserNotifications: val })}
          />
          <Toggle
            label="Sound Alerts"
            description="Play sound for important alerts"
            checked={settings.soundAlerts ?? false}
            onChange={(val) => onChange({ ...settings, soundAlerts: val })}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Notification Email</label>
        <Input
          type="email"
          value={settings.notificationEmail || ''}
          onChange={(e) => onChange({ ...settings, notificationEmail: e.target.value })}
          placeholder="notifications@example.com"
        />
      </div>
    </div>
  );
}

// Users Settings Section
function UsersSettings({ users, onInvite, onEdit, onRemove }) {
  const [showInviteModal, setShowInviteModal] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-slate-800">Team Members</h3>
          <p className="text-sm text-slate-500">{users.length} users with access</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Invite User
        </Button>
      </div>

      <div className="space-y-3">
        {users.map(user => (
          <UserRow
            key={user.id}
            user={user}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        ))}
      </div>

      <div className="pt-4 border-t border-slate-200">
        <h4 className="font-medium text-slate-800 mb-3">Role Descriptions</h4>
        <div className="space-y-2">
          {roleOptions.map(role => (
            <div key={role.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Badge variant="neutral">{role.label}</Badge>
              <span className="text-sm text-slate-600">{role.description}</span>
            </div>
          ))}
        </div>
      </div>

      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvite={(data) => {
            onInvite(data);
            setShowInviteModal(false);
          }}
        />
      )}
    </div>
  );
}

// Invite User Modal
function InviteUserModal({ onClose, onInvite }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');

  const handleSubmit = (e) => {
    e.preventDefault();
    onInvite({ email, role });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Invite User</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
            <Select value={role} onChange={(e) => setRole(e.target.value)}>
              {roleOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </Select>
          </div>
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit">Send Invite</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Integrations Settings Section
function IntegrationsSettings({ settings, onChange }) {
  const integrations = [
    { id: 'mylaps', name: 'MyLaps', description: 'Transponder timing integration', connected: true, icon: '⏱️' },
    { id: 'speedhive', name: 'Speedhive', description: 'Live timing and scoring', connected: true, icon: '📊' },
    { id: 'hubspot', name: 'HubSpot', description: 'CRM and marketing automation', connected: false, icon: '📧' },
    { id: 'stripe', name: 'Stripe', description: 'Payment processing', connected: true, icon: '💳' },
    { id: 'fantasy', name: 'Fantasy Racing', description: 'Fantasy league integration', connected: false, icon: '🏆' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Connect external services to enhance functionality.</p>

      <div className="space-y-3">
        {integrations.map(integration => (
          <div
            key={integration.id}
            className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-xl">
                {integration.icon}
              </div>
              <div>
                <div className="font-medium text-slate-800">{integration.name}</div>
                <div className="text-sm text-slate-500">{integration.description}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {integration.connected ? (
                <>
                  <Badge variant="success">Connected</Badge>
                  <Button variant="secondary" size="sm">Configure</Button>
                </>
              ) : (
                <Button size="sm">Connect</Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-slate-200">
        <h4 className="font-medium text-slate-800 mb-3">API Access</h4>
        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-medium text-slate-700">API Key</div>
              <div className="text-sm text-slate-500">Use this key for external integrations</div>
            </div>
            <Button variant="secondary" size="sm">
              <RefreshCw className="w-4 h-4 mr-1" />
              Regenerate
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="password"
              value="sk_live_xxxxxxxxxxxxxxxxxxxxx"
              readOnly
              className="font-mono text-sm"
            />
            <Button variant="secondary" size="sm">
              <Eye className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Appearance Settings Section
function AppearanceSettings({ settings, onChange, currentSeries }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Primary Color</label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={settings.primaryColor || currentSeries?.color || '#0c7abf'}
            onChange={(e) => onChange({ ...settings, primaryColor: e.target.value })}
            className="w-12 h-12 rounded-lg border border-slate-200 cursor-pointer"
          />
          <Input
            value={settings.primaryColor || currentSeries?.color || '#0c7abf'}
            onChange={(e) => onChange({ ...settings, primaryColor: e.target.value })}
            placeholder="#0c7abf"
            className="w-32 font-mono"
          />
          <div
            className="flex-1 h-12 rounded-lg"
            style={{ backgroundColor: settings.primaryColor || currentSeries?.color || '#0c7abf' }}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Series Logo</label>
        <div className="flex items-center gap-4">
          {(settings.logoUrl || currentSeries?.logo) && (
            <img
              src={settings.logoUrl || currentSeries?.logo}
              alt="Series logo"
              className="h-16 object-contain bg-white rounded-lg border border-slate-200 p-2"
            />
          )}
          <div className="flex-1">
            <Input
              value={settings.logoUrl || ''}
              onChange={(e) => onChange({ ...settings, logoUrl: e.target.value })}
              placeholder="https://example.com/logo.png"
            />
          </div>
          <Button variant="secondary">
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </Button>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h4 className="font-medium text-slate-800 mb-4">Display Options</h4>
        <div className="space-y-1">
          <Toggle
            label="Show Series Logo in Header"
            checked={settings.showLogoInHeader ?? true}
            onChange={(val) => onChange({ ...settings, showLogoInHeader: val })}
          />
          <Toggle
            label="Use Series Color for Accents"
            checked={settings.useSeriesColor ?? true}
            onChange={(val) => onChange({ ...settings, useSeriesColor: val })}
          />
          <Toggle
            label="Compact Table View"
            checked={settings.compactTables ?? false}
            onChange={(val) => onChange({ ...settings, compactTables: val })}
          />
        </div>
      </div>
    </div>
  );
}

// Data Management Settings Section
function DataSettings({ settings, onChange }) {
  const backups = [
    { id: 1, date: '2025-12-10T06:00:00', size: '245 MB', type: 'auto' },
    { id: 2, date: '2025-12-09T06:00:00', size: '243 MB', type: 'auto' },
    { id: 3, date: '2025-12-08T14:30:00', size: '242 MB', type: 'manual' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-slate-800 mb-3">Import Data</h4>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="secondary" className="justify-start">
            <Upload className="w-4 h-4 mr-2" />
            Import Drivers (CSV)
          </Button>
          <Button variant="secondary" className="justify-start">
            <Upload className="w-4 h-4 mr-2" />
            Import Events (CSV)
          </Button>
          <Button variant="secondary" className="justify-start">
            <Upload className="w-4 h-4 mr-2" />
            Import Results (CSV)
          </Button>
          <Button variant="secondary" className="justify-start">
            <Upload className="w-4 h-4 mr-2" />
            Import Points (CSV)
          </Button>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium text-slate-800">Backups</h4>
            <p className="text-sm text-slate-500">Automatic backups run daily at 6:00 AM ET</p>
          </div>
          <Button variant="secondary">
            <Database className="w-4 h-4 mr-2" />
            Create Backup
          </Button>
        </div>
        <div className="space-y-2">
          {backups.map(backup => (
            <div
              key={backup.id}
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <Database className="w-4 h-4 text-slate-400" />
                <div>
                  <div className="text-sm font-medium text-slate-700">
                    {new Date(backup.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </div>
                  <div className="text-xs text-slate-500">
                    {backup.size} • {backup.type === 'auto' ? 'Automatic' : 'Manual'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm">Download</Button>
                <Button variant="secondary" size="sm">Restore</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h4 className="font-medium text-red-600 mb-3">Danger Zone</h4>
        <div className="p-4 rounded-lg bg-red-50 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-red-800">Reset Season Data</div>
              <div className="text-sm text-red-600">Clear all entries, results, and points for the current season</div>
            </div>
            <Button variant="secondary" className="text-red-600 border-red-200 hover:bg-red-100">
              Reset Data
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Security Settings Section
function SecuritySettings({ settings, onChange }) {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-slate-800 mb-3">Authentication</h4>
        <div className="space-y-1">
          <Toggle
            label="Two-Factor Authentication"
            description="Require 2FA for all team members"
            checked={settings.require2FA ?? false}
            onChange={(val) => onChange({ ...settings, require2FA: val })}
          />
          <Toggle
            label="Single Sign-On (SSO)"
            description="Allow SSO via company identity provider"
            checked={settings.enableSSO ?? false}
            onChange={(val) => onChange({ ...settings, enableSSO: val })}
          />
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h4 className="font-medium text-slate-800 mb-3">Session Settings</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Session Timeout</label>
            <Select
              value={settings.sessionTimeout || '24'}
              onChange={(e) => onChange({ ...settings, sessionTimeout: e.target.value })}
            >
              <option value="1">1 hour</option>
              <option value="8">8 hours</option>
              <option value="24">24 hours</option>
              <option value="168">7 days</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Max Sessions</label>
            <Select
              value={settings.maxSessions || '3'}
              onChange={(e) => onChange({ ...settings, maxSessions: e.target.value })}
            >
              <option value="1">1 device</option>
              <option value="3">3 devices</option>
              <option value="5">5 devices</option>
              <option value="unlimited">Unlimited</option>
            </Select>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h4 className="font-medium text-slate-800 mb-3">Activity Log</h4>
        <div className="space-y-2">
          {[
            { action: 'Login', user: 'Chris Owens', time: '10 minutes ago', ip: '192.168.1.1' },
            { action: 'Settings Updated', user: 'Chris Owens', time: '2 hours ago', ip: '192.168.1.1' },
            { action: 'User Invited', user: 'Chris Owens', time: '1 day ago', ip: '192.168.1.1' },
            { action: 'Login', user: 'John Smith', time: '2 days ago', ip: '192.168.1.45' },
          ].map((log, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-700">{log.action}</div>
                  <div className="text-xs text-slate-500">{log.user} • {log.ip}</div>
                </div>
              </div>
              <span className="text-xs text-slate-500">{log.time}</span>
            </div>
          ))}
        </div>
        <Button variant="secondary" className="w-full mt-3">View Full Activity Log</Button>
      </div>
    </div>
  );
}

export default function Settings() {
  useParams();
  const currentSeries = useSeries();

  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState({
    displayName: currentSeries?.name || '',
    shortName: currentSeries?.shortName || '',
    currentSeason: '2026',
    timezone: 'America/New_York',
    description: '',
    eventReminders: true,
    entryNotifications: true,
    resultsNotifications: true,
    weeklySummary: false,
    browserNotifications: true,
    soundAlerts: false,
    primaryColor: currentSeries?.color || '#0c7abf',
    logoUrl: currentSeries?.logo || '',
    showLogoInHeader: true,
    useSeriesColor: true,
    compactTables: false,
    require2FA: false,
    enableSSO: false,
    sessionTimeout: '24',
    maxSessions: '3',
  });
  const [users, setUsers] = useState(mockUsers);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleInviteUser = (data) => {
    setUsers(prev => [...prev, {
      id: Date.now(),
      name: data.email.split('@')[0],
      email: data.email,
      role: data.role.charAt(0).toUpperCase() + data.role.slice(1),
      status: 'invited',
      lastLogin: null,
    }]);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'general':
        return <GeneralSettings settings={settings} onChange={setSettings} />;
      case 'notifications':
        return <NotificationsSettings settings={settings} onChange={setSettings} />;
      case 'users':
        return (
          <UsersSettings
            users={users}
            onInvite={handleInviteUser}
            onEdit={(user) => console.log('Edit user:', user)}
            onRemove={(user) => setUsers(prev => prev.filter(u => u.id !== user.id))}
          />
        );
      case 'integrations':
        return <IntegrationsSettings settings={settings} onChange={setSettings} />;
      case 'appearance':
        return <AppearanceSettings settings={settings} onChange={setSettings} currentSeries={currentSeries} />;
      case 'data':
        return <DataSettings settings={settings} onChange={setSettings} />;
      case 'security':
        return <SecuritySettings settings={settings} onChange={setSettings} />;
      default:
        return <GeneralSettings settings={settings} onChange={setSettings} />;
    }
  };

  const currentSectionData = settingsSections.find(s => s.id === activeSection);

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h1 className="page-title">Settings</h1>
            <p className="page-subtitle">Manage series configuration and preferences</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm">Saved</span>
            </div>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-2">
              <nav className="space-y-1">
                {settingsSections.map(section => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        isActive
                          ? 'bg-rms-accent/10 text-rms-accent'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{section.name}</span>
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>{currentSectionData?.name}</CardTitle>
              <CardDescription>{currentSectionData?.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {renderSection()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
